import { composeFetch } from '@library/http'
import {
  addPath,
  composeRequest,
  goustoHeaders,
  sendJSON,
  setMethod,
  setServiceUrl,
} from '@library/http/requests'

import type { DisplayPreference, MenuPreferences } from './interfaces/preferences'

type GetMenuPreferencesInput = {
  userId: string
}

type Result<T, U> = { type: 'success'; value: T } | { type: 'failure'; error: U }

export const CALORIES_DISPLAY_PREFERENCE_TYPE = 'calories'

export async function jsonParseMenuPreferences(resp: string): Promise<Result<unknown, Error>> {
  try {
    const value = await JSON.parse(resp)

    return { type: 'success', value }
  } catch (parseErr) {
    return { type: 'failure', error: parseErr as Error }
  }
}

const menuPreferencesParser = async (response: Response): Promise<MenuPreferences> => {
  if (!response.ok) {
    throw new Error('Error fetching menu preferences')
  }

  const responseText = await response.text()
  const parsedResponse = await jsonParseMenuPreferences(responseText)

  if (parsedResponse.type === 'failure') {
    throw parsedResponse.error
  }

  return parsedResponse.value as MenuPreferences
}

const menuPreferencesRequest = composeRequest<GetMenuPreferencesInput>(
  setServiceUrl('menuexperience', 1),
  addPath('preferences'),
  setMethod('GET'),
  goustoHeaders,
)

export const menuPreferencesFetcher = composeFetch(menuPreferencesRequest, menuPreferencesParser)

const updateMenuPreferencesParser = async (response: Response): Promise<null> => {
  if (!response.ok) {
    throw new Error('Error updating menu preferences')
  }

  return null
}

const updateMenuPreferencesRequest = composeRequest<MenuPreferences>(
  setServiceUrl('menuexperience', 1),
  addPath('preferences'),
  setMethod('PUT'),
  sendJSON<MenuPreferences>(),
  goustoHeaders,
)

export const updateMenuPreferences = composeFetch(
  updateMenuPreferencesRequest,
  updateMenuPreferencesParser,
)

export const isCaloriePreferenceSelected = (
  displayPreferences: DisplayPreference[] | undefined,
  defaultSelected = true,
): boolean => {
  const preference = displayPreferences?.find(
    (displayPreference) => displayPreference.type === CALORIES_DISPLAY_PREFERENCE_TYPE,
  )

  return preference?.selected ?? defaultSelected
}
